import Decimal from "decimal.js"

// Configure Decimal.js to match Ruby's default rounding (half up)
Decimal.set({ rounding: Decimal.ROUND_HALF_UP })

/**
 * Bezpiecznie konwertuje input na Decimal.
 * Obsługuje stringi, liczby, null, undefined oraz próbuje sparsować stringi zawierające spacje lub przecinki jako liczby.
 * @param {number|string|null|undefined} input - Wartość do konwersji.
 * @param {Decimal} [defaultValue=new Decimal(0)] - Wartość domyślna, jeśli konwersja się nie powiedzie.
 * @returns {Decimal} Obiekt Decimal.
 */
const toDecimalObject = (input, defaultValue = new Decimal(0)) => {
  if (input === null || input === undefined || (typeof input === "number" && isNaN(input))) {
    return defaultValue
  }
  let valueStr = String(input)
  if (typeof input === "string") {
    valueStr = valueStr.replace(/ /g, "").replace(/,/g, ".")
  }

  // Nasladujemy .to_f z Ruby: zatrzymujemy parsowanie przy pierwszym nieprawidłowym znaku.
  const floatVal = parseFloat(valueStr)

  // Jeśli parseFloat się nie powiodło (zwrócił NaN), oznacza to, że początkowa część nie była poprawną liczbą
  if (isNaN(floatVal)) {
    return defaultValue
  }

  try {
    return new Decimal(floatVal)
  } catch (_e) {
    return defaultValue
  }
}

const toDecimal = (input, defaultValue = new Decimal(0)) => {
  return toDecimalObject(input, defaultValue).toNumber()
}

/**
 * Zaokragla liczbe (lub Decimal) do okreslonej precyzji uzywajac Decimal.js.
 * Nasladauje FNumbers.round_price, w tym parsowanie stringow.
 * @param {number|string|null|undefined|Decimal} num - Liczba lub string do zaokraglenia.
 * @param {number} [precision=2] - Liczba miejsc po przecinku.
 * @returns {number} Zaokraglona liczba (jako standardowy JS number).
 * @example
 * roundPrice("12,123456") // 12.12
 * roundPrice("12.005") // 12.01
 * roundPrice("12.004") // 12.00
 * roundPrice("0.500012.004 000 001") // 0.50 (parseFloat)
 * roundPrice(null) // 0.0
 * roundPrice(NaN) // 0.0
 * roundPrice("12.004", 15) // 12.004
 */
const roundPrice = (num, precision = 2) => {
  const decimalValue = toDecimalObject(num)

  return decimalValue.toDecimalPlaces(precision).toNumber()
}

/**
 * Oblicza współczynnik konwertera podatków (1 + tax%/100 + tax2%/100) uzywajac Decimal.js.
 * Nasladauje FNumbers.taxes_converter, zaokraglając podatki przed obliczeniem.
 * @param {number|string|null|undefined|Decimal} tax - Podstawowy podatek (procent).
 * @param {number|string|null|undefined|Decimal} [tax2=0] - Dodatkowy podatek (procent).
 * @returns {number} Współczynnik konwertera podatków (jako standardowy JS number).
 * @example
 * taxesConverter(10) // 1.1
 * taxesConverter(10, 15) // 1.25
 * taxesConverter("8.333") // 1.0833 (8.333 rounds to 8.33)
 */
const taxesConverter = (tax, tax2 = 0) => {
  // FNumbers zaokragla podatki przed obliczeniem (do 2 miejsc po przecinku)
  const roundedTax = toDecimalObject(tax).toDecimalPlaces(2)
  const roundedTax2 = toDecimalObject(tax2).toDecimalPlaces(2)

  const oneHundred = new Decimal(100)
  const one = new Decimal(1)

  const result = one.plus(roundedTax.dividedBy(oneHundred)).plus(roundedTax2.dividedBy(oneHundred))

  return result.toNumber()
}

/**
 * Oblicza cene brutto z ceny netto i stawki podatkowej uzywajac Decimal.js.
 * Nasladauje FNumbers.gross_from_net, w tym logiczne sprawdzenie gross_check.
 * @param {number|string|null|undefined|Decimal} net - Cena netto.
 * @param {number|string|null|undefined|Decimal} tax - Podstawowy podatek (procent).
 * @param {number|string|null|undefined|Decimal} [tax2=0] - Dodatkowy podatek (procent).
 * @param {number|string|null|undefined|Decimal} [grossCheck=null] - Opcjonalna cena brutto do sprawdzenia.
 * @param {number} [precision=2] - Precyzja wyniku.
 * @returns {number} Obliczona cena brutto (jako standardowy JS number).
 * @example
 * grossFromNet(100, 10) // 110.00
 * grossFromNet(9.93, 23) // 12.21
 * grossFromNet(9.93, 23, 0, 12.22) // 12.22 (grossCheck used)
 * grossFromNet(9.93, 23, 0, 12.23) // 12.21 (grossCheck ignored)
 * grossFromNet(9.935, 23, 0, null, 3) // 12.220 (precision 3)
 */
const grossFromNet = (net, tax, tax2 = 0, grossCheck = null, precision = 2) => {
  const inputNetDecimal = toDecimalObject(net)
  const netRounded = inputNetDecimal.toDecimalPlaces(precision) // Zaokragla cene netto dla porównania

  // Porownanie ceny brutto z cena netto
  if (grossCheck !== null && grossCheck !== undefined) {
    const calculatedNetDecimal = netFromGrossInternal(grossCheck, tax, tax2, null, precision) // Uzycie wersji wewnetrznej
    if (calculatedNetDecimal.equals(netRounded)) {
      return toDecimalObject(grossCheck).toDecimalPlaces(precision).toNumber()
    }
  }

  const converter = new Decimal(taxesConverter(tax, tax2))
  const result = inputNetDecimal.times(converter)
  return result.toDecimalPlaces(precision).toNumber()
}

// Wewnetrzna wersja uzywana do logicznego sprawdzenia ceny brutto
const netFromGrossInternal = (gross, tax, tax2 = 0, netCheck = null, precision = 2) => {
  const inputGrossDecimal = toDecimalObject(gross)
  const grossRounded = inputGrossDecimal.toDecimalPlaces(precision)

  if (netCheck !== null && netCheck !== undefined) {
    const calculatedGrossDecimal = grossFromNet(netCheck, tax, tax2, null, precision)
    if (new Decimal(calculatedGrossDecimal).equals(grossRounded)) {
      return toDecimalObject(netCheck).toDecimalPlaces(precision) // Zwraca Decimal
    }
  }

  const converter = new Decimal(taxesConverter(tax, tax2))
  if (converter.isZero()) return new Decimal(0)

  const result = inputGrossDecimal.dividedBy(converter)
  return result.toDecimalPlaces(precision) // Return Decimal
}

/**
 * Oblicza cene netto z ceny brutto i stawki podatkowej uzywajac Decimal.js.
 * Nasladauje FNumbers.net_from_gross, w tym logiczne sprawdzenie net_check.
 * @param {number|string|null|undefined|Decimal} gross - Cena brutto.
 * @param {number|string|null|undefined|Decimal} tax - Podstawowy podatek (procent).
 * @param {number|string|null|undefined|Decimal} [tax2=0] - Dodatkowy podatek (procent).
 * @param {number|string|null|undefined|Decimal} [netCheck=null] - Opcjonalna cena netto do sprawdzenia.
 * @param {number} [precision=2] - Precyzja wyniku.
 * @returns {number} Obliczona cena netto (jako standardowy JS number).
 * @example
 * netFromGross(110, 10) // 100.00
 * netFromGross(12.21, 23) // 9.93
 * netFromGross(12.22, 23) // 9.93
 * netFromGross(12.22, 23, 0, 9.934959) // 9.93 (netCheck used)
 * netFromGross(12.22, 23, 0, null, 5) // 9.93496 (precision 5)
 */
const netFromGross = (gross, tax, tax2 = 0, netCheck = null, precision = 2) => {
  return netFromGrossInternal(gross, tax, tax2, netCheck, precision).toNumber()
}

/**
 * Oblicza cene calkowita lub jednostkowa uzywajac Decimal.js.
 * Nasladauje FNumbers.to_quantity, stosując sprawdzenia spojnosci na podstawie zaokraglania.
 * @param {number|string|null|undefined|Decimal} unit - Cena jednostkowa.
 * @param {number|string|null|undefined|Decimal} q - Ilość.
 * @param {number|string|null|undefined|Decimal} total - Cena całkowita.
 * @param {'to_total'|'to_unit'} kind - Określa kierunek obliczenia.
 * @param {boolean} [force=false] - Jeśli true, wymusza ponowne obliczenie.
 * @param {number} [precision=2] - Precyzja wyniku (i dla obliczenia jednostkowej ceny w 'to_unit').
 * @returns {number} Obliczona cena całkowita lub jednostkowa (jako standardowy JS number).
 * @example
 * toQuantity(10, 10, 100.00, 'to_total') // 100.00 (spojne, zwraca cene calkowita)
 * toQuantity(10.005, 10, 100.00, 'to_total') // 100.05 (niespojne, zwraca ponownie obliczona cene calkowita)
 * toQuantity(10.00, 10, 100.00, 'to_unit') // 10.00 (spojne, zwraca cene jednostkowa)
 * toQuantity(10.00, 10, 100.05, 'to_unit') // 10.01 (niespojne, zwraca ponownie obliczona cene jednostkowa)
 * toQuantity(10.00, 3, 30.01, 'to_unit', false, 3) // 10.003 (precision 3)
 */
const toQuantity = (unit, q, total, kind, force = false, precision = 2) => {
  const unitDecimal = toDecimalObject(unit)
  const qDecimal = toDecimalObject(q)
  const totalDecimal = toDecimalObject(total)

  // Wersja Ruby wykonuje porównania/obliczenia pośrednie z domyslną precyzja 2
  const intermediatePrecision = 2

  if (kind === "to_total") {
    const calculatedTotal = unitDecimal.times(qDecimal).toDecimalPlaces(intermediatePrecision)

    if (force || !totalDecimal.toDecimalPlaces(intermediatePrecision).equals(calculatedTotal)) {
      return unitDecimal.times(qDecimal).toDecimalPlaces(precision).toNumber()
    } else {
      return totalDecimal.toDecimalPlaces(precision).toNumber()
    }
  } else if (kind === "to_unit") {
    if (qDecimal.isZero()) return new Decimal(0).toDecimalPlaces(precision).toNumber()

    const calculatedTotalFromUnit = unitDecimal.times(qDecimal).toDecimalPlaces(intermediatePrecision)

    if (force || !totalDecimal.toDecimalPlaces(intermediatePrecision).equals(calculatedTotalFromUnit)) {
      return totalDecimal.dividedBy(qDecimal).toDecimalPlaces(precision).toNumber()
    } else {
      return unitDecimal.toDecimalPlaces(precision).toNumber()
    }
  } else {
    throw new Error(`zly kind!!!: ${kind}`)
  }
}

export {
  roundPrice,
  taxesConverter,
  grossFromNet,
  netFromGross,
  toQuantity,
  toDecimal,
}
